import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Wallet, AlertTriangle, TrendingUp, Loader2, ExternalLink,
  Calendar, Users, DollarSign, ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/dashboard");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const agingTotal = Object.values(data.aging).reduce((a, b) => a + b, 0) || 1;
  const bucketPct = (v) => Math.round((v / agingTotal) * 100);

  return (
    <div>
      <PageHeader
        title="Cari Hesap Merkezi"
        subtitle="Tahsilat performansı, gecikme yaşlandırma ve müşteri risk paneli"
        breadcrumbs={["Ana Sayfa", "Cari Hesap"]}
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Top KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cari-kpi-strip">
          <TopKpi color="emerald" icon={Calendar} label="Bugün Tahsilat" value={`₺${fmt(data.today_due)}`} testId="kpi-today-due" />
          <TopKpi color="amber" icon={Calendar} label="7 Gün İçinde" value={`₺${fmt(data.week_due)}`} testId="kpi-week-due" />
          <TopKpi color="red" icon={AlertTriangle} label="Vadesi Geçmiş" value={`₺${fmt(data.overdue_total)}`}
            sub={`${data.overdue_customers_count} müşteri`} testId="kpi-overdue-total" />
          <TopKpi color="blue" icon={Wallet} label="Toplam Açık" value={`₺${fmt(data.total_outstanding)}`} testId="kpi-total-outstanding" />
          <TopKpi color="purple" icon={TrendingUp} label="Son 30g Tahsil" value={`₺${fmt(data.collected_last_30d)}`} testId="kpi-collected-30d" />
        </div>

        {/* Ageing analysis */}
        <Card className="rounded-sm bg-card border-border p-5" data-testid="aging-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-blue-500" />
            <div className="text-sm font-bold uppercase tracking-wider">Gecikme Yaşlandırma Analizi</div>
          </div>
          <div className="space-y-3">
            <AgingRow label="0 – 30 gün" value={data.aging["0_30"]} pct={bucketPct(data.aging["0_30"])} color="amber" testId="aging-0-30" />
            <AgingRow label="31 – 60 gün" value={data.aging["31_60"]} pct={bucketPct(data.aging["31_60"])} color="orange" testId="aging-31-60" />
            <AgingRow label="61 – 90 gün" value={data.aging["61_90"]} pct={bucketPct(data.aging["61_90"])} color="red" testId="aging-61-90" />
            <AgingRow label="90+ gün · KRİTİK" value={data.aging["90_plus"]} pct={bucketPct(data.aging["90_plus"])} color="darkred" testId="aging-90-plus" />
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top overdue */}
          <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="top-overdue-card">
            <div className="px-4 py-3 border-b border-border bg-red-500/5 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <div className="text-sm font-bold uppercase tracking-wider text-red-500">En Çok Geciken Müşteriler</div>
              <Badge className="ml-auto bg-red-500/15 text-red-500 border-red-500/40 rounded-sm text-[10px]">
                {data.top_overdue_customers.length}
              </Badge>
            </div>
            {data.top_overdue_customers.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Vadesi geçmiş müşteri yok 🎉</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-widest font-mono">Müşteri</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Gecikmiş</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Açık</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_overdue_customers.map((c) => (
                    <TableRow key={c.customer_id} className="border-border cursor-pointer hover:bg-muted/40"
                      onClick={() => nav(`/musteri/${c.customer_id}`)}
                      data-testid={`overdue-customer-${c.customer_id}`}>
                      <TableCell className="text-sm font-medium">{c.customer_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-red-500">₺{fmt(c.overdue)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">₺{fmt(c.outstanding)}</TableCell>
                      <TableCell><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Top open balance */}
          <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="top-open-card">
            <div className="px-4 py-3 border-b border-border bg-blue-500/5 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              <div className="text-sm font-bold uppercase tracking-wider text-blue-500">En Yüksek Açık Bakiye</div>
              <Badge className="ml-auto bg-blue-500/15 text-blue-500 border-blue-500/40 rounded-sm text-[10px]">
                {data.top_open_balance_customers.length}
              </Badge>
            </div>
            {data.top_open_balance_customers.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Açık bakiye yok</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-widest font-mono">Müşteri</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Açık Bakiye</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_open_balance_customers.slice(0, 10).map((c) => (
                    <TableRow key={c.customer_id} className="border-border cursor-pointer hover:bg-muted/40"
                      onClick={() => nav(`/musteri/${c.customer_id}`)}>
                      <TableCell className="text-sm font-medium">{c.customer_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold">₺{fmt(c.outstanding)}</TableCell>
                      <TableCell><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        <div className="text-center pt-4">
          <Button variant="outline" onClick={() => nav("/musteri")} className="rounded-sm">
            Tüm Müşteriler <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TopKpi({ icon: Icon, color, label, value, sub, testId }) {
  const bg = {
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
    red: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30",
  }[color] || "bg-blue-500/10 text-blue-600 border-blue-500/30";
  return (
    <Card className="rounded-sm bg-card border-border p-4" data-testid={testId}>
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center border mb-2 ${bg}`}>
        <Icon className="w-4 h-4" strokeWidth={2.5} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground truncate">{label}</div>
      <div className="text-xl font-bold mt-1 font-mono truncate">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

function AgingRow({ label, value, pct, color, testId }) {
  const barCls = {
    amber: "bg-amber-500", orange: "bg-blue-500", red: "bg-red-500", darkred: "bg-red-700",
  }[color];
  return (
    <div className="grid grid-cols-[1fr_auto_2fr_auto] gap-3 items-center" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{label}</div>
      <div className="text-sm font-mono font-bold text-right">₺{fmt(value)}</div>
      <div className="h-2 rounded-sm bg-muted overflow-hidden">
        <div className={`h-full ${barCls} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs font-mono text-muted-foreground text-right w-10">{pct}%</div>
    </div>
  );
}
